<template>
  <div class="contract-preview">
    <div class="header-actions">
      <el-button type="primary" :loading="downloading" @click="downloadPDF" :disabled="!renderedContent">下载PDF</el-button>
      <el-button :loading="downloading" @click="downloadImage" :disabled="!renderedContent">下载图片</el-button>
      <el-button @click="printContract" :disabled="!renderedContent">打印</el-button>
      <el-button @click="goBack">返回</el-button>
    </div>

    <p class="capture-tip">PDF 由当前预览页面完整截图生成，与屏幕所见一致（单页长图，不分页）。</p>

    <div
      ref="captureBox"
      class="contract-content"
      :class="{ 'is-capturing': downloading }"
      v-loading="loading"
    >
      <div class="contract-paper" v-if="renderedContent" v-html="renderedContent"></div>
      <div v-else-if="!loading" class="no-content">
        <el-empty description="暂无合同内容" />
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

export default {
  name: 'ContractPreview',
  data() {
    return {
      loading: false,
      downloading: false,
      renderedContent: '',
      sealImageDataUri: '',
      orderUuid: '',
      templateName: '',
    }
  },
  methods: {
    async loadContract() {
      this.loading = true
      try {
        const res = await this.$http.request('renderContract', {
          order_uuid: this.orderUuid,
        })
        if (!res || !res.data) {
          this.$message.error('获取合同内容失败')
          return
        }
        this.sealImageDataUri = res.data.seal_image_data_uri || ''
        this.templateName = res.data.template_name || ''
        this.renderedContent = await this.inlineImagesInHtml(res.data.content || '')
        await this.$nextTick()
        await this.applySealToDom()
      } catch (err) {
        const msg = err && err.message ? err.message : '获取合同内容失败'
        this.$message.error(msg)
      } finally {
        this.loading = false
      }
    },

    async fetchImageDataUri(url) {
      if (!url) return ''
      if (url.indexOf('data:image') === 0) return url
      try {
        const res = await this.$http.request('contractImageDataUri', { url })
        return (res && res.data && res.data.data_uri) ? res.data.data_uri : ''
      } catch (e) {
        return ''
      }
    },

    async inlineImagesInHtml(html) {
      if (!html) return ''
      const doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html')
      const root = doc.body.firstChild
      if (!root) return html

      const imgs = root.querySelectorAll('img')
      await Promise.all(Array.from(imgs).map(async (img) => {
        const src = img.getAttribute('src') || ''
        if (!src || src.indexOf('data:image') === 0) return
        const dataUri = await this.fetchImageDataUri(src)
        if (dataUri) {
          img.setAttribute('src', dataUri)
        }
      }))

      return root.innerHTML
    },

    loadImageElement(src) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
      })
    },

    async applySealToDom() {
      const target = this.$refs.captureBox
      if (!target || !this.sealImageDataUri) return

      const sealImgs = target.querySelectorAll('img.contract-seal-img, .contract-seal img, img[alt="电子章"]')
      sealImgs.forEach((img) => {
        img.setAttribute('src', this.sealImageDataUri)
        img.removeAttribute('crossorigin')
      })

      await this.waitForImages(target)
    },

    waitForImages(container) {
      const imgs = container.querySelectorAll('img')
      const tasks = Array.from(imgs).map(img => {
        if (img.complete && img.naturalWidth > 0) {
          return Promise.resolve()
        }
        return new Promise(resolve => {
          img.onload = resolve
          img.onerror = resolve
          setTimeout(resolve, 8000)
        })
      })
      return Promise.all(tasks)
    },

    async inlineImagesForCapture(container) {
      const imgs = Array.from(container.querySelectorAll('img'))
      await Promise.all(imgs.map(async (img) => {
        let src = img.getAttribute('src') || img.src || ''
        if (!src || src.indexOf('data:image') === 0) return

        const dataUri = await this.fetchImageDataUri(src)
        if (dataUri) {
          img.setAttribute('src', dataUri)
          await new Promise(resolve => {
            img.onload = resolve
            img.onerror = resolve
            setTimeout(resolve, 8000)
          })
        }
      }))
    },

    /** html2canvas 可能漏绘图片，用 canvas 按位置手动叠加 */
    async overlayImagesOnCanvas(canvas, target, scale) {
      const ctx = canvas.getContext('2d')
      if (!ctx) return canvas

      const rootRect = target.getBoundingClientRect()
      const imgs = target.querySelectorAll('img')

      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i]
        let drawSrc = img.getAttribute('src') || img.src || ''

        if (!drawSrc || (img.naturalWidth === 0)) {
          if (img.classList.contains('contract-seal-img') && this.sealImageDataUri) {
            drawSrc = this.sealImageDataUri
          } else {
            continue
          }
        }

        if (drawSrc.indexOf('data:image') !== 0) {
          const dataUri = await this.fetchImageDataUri(drawSrc)
          if (dataUri) drawSrc = dataUri
        }

        let drawImg = img
        if (drawSrc !== img.src || img.naturalWidth === 0) {
          try {
            drawImg = await this.loadImageElement(drawSrc)
          } catch (e) {
            continue
          }
        }

        const rect = img.getBoundingClientRect()
        if (rect.width <= 0 || rect.height <= 0) continue

        const x = (rect.left - rootRect.left) * scale
        const y = (rect.top - rootRect.top) * scale
        const w = rect.width * scale
        const h = rect.height * scale

        try {
          ctx.drawImage(drawImg, x, y, w, h)
        } catch (e) {
          console.warn('drawImage failed', e)
        }
      }

      return canvas
    },

    async capturePreviewCanvas() {
      const target = this.$refs.captureBox
      if (!target) {
        throw new Error('capture target missing')
      }

      await this.$nextTick()
      await this.applySealToDom()
      await this.inlineImagesForCapture(target)
      await this.waitForImages(target)

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      window.scrollTo(0, 0)

      const scale = 2
      const options = {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        width: target.scrollWidth,
        height: target.scrollHeight,
        windowWidth: target.scrollWidth,
        windowHeight: target.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => {
          const clonedTarget = clonedDoc.querySelector('.contract-content')
          if (!clonedTarget) return
          clonedTarget.querySelectorAll('img').forEach((img) => {
            const src = img.getAttribute('src') || ''
            if (src.indexOf('data:image') === 0) {
              img.src = src
            } else if (this.sealImageDataUri && img.classList.contains('contract-seal-img')) {
              img.src = this.sealImageDataUri
            }
          })
        },
      }

      let canvas
      try {
        canvas = await html2canvas(target, options)
      } catch (err) {
        canvas = await html2canvas(target, Object.assign({}, options, { scale: 1 }))
      }

      canvas = await this.overlayImagesOnCanvas(canvas, target, scale)

      window.scrollTo(0, scrollTop)
      return canvas
    },

    async downloadPDF() {
      if (!this.renderedContent) return
      this.downloading = true
      try {
        const canvas = await this.capturePreviewCanvas()
        const imgData = canvas.toDataURL('image/png')
        const pdfWidth = canvas.width
        const pdfHeight = canvas.height

        const pdf = new jsPDF({
          orientation: pdfWidth >= pdfHeight ? 'landscape' : 'portrait',
          unit: 'px',
          format: [pdfWidth, pdfHeight],
          compress: true,
        })

        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST')
        pdf.save(`旅游合同_${this.orderUuid}.pdf`)
        this.$message.success('PDF 下载成功')
      } catch (err) {
        console.error('PDF生成失败:', err)
        this.$message.error('PDF 生成失败，可尝试下载图片')
      } finally {
        this.downloading = false
      }
    },

    async downloadImage() {
      if (!this.renderedContent) return
      this.downloading = true
      try {
        const canvas = await this.capturePreviewCanvas()
        const link = document.createElement('a')
        link.href = canvas.toDataURL('image/png')
        link.download = `旅游合同_${this.orderUuid}.png`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        this.$message.success('图片下载成功')
      } catch (err) {
        console.error('图片生成失败:', err)
        this.$message.error('图片生成失败，请重试')
      } finally {
        this.downloading = false
      }
    },

    async printContract() {
      if (!this.renderedContent) return

      const captureBox = this.$refs.captureBox
      if (!captureBox) return

      await this.$nextTick()
      await this.applySealToDom()

      const paperEl = captureBox.querySelector('.contract-paper') || captureBox

      const printWin = window.open('', '_blank', 'noopener,noreferrer')
      if (!printWin) {
        this.$message.warning('请允许浏览器弹窗后重试打印')
        return
      }

      const styles = `
        @page { margin: 12mm; size: A4; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body {
          font-size: 12px;
          line-height: 1.6;
          color: #333;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        table { width: 100%; border-collapse: collapse; margin: 8px 0; }
        td, th { border: 1px solid #ddd; padding: 6px 10px; line-height: 1.5; }
        p { margin: 0 0 6px 0; }
        img { max-width: 100%; height: auto; }
        .contract-seal-img {
          max-width: 100px;
          height: auto;
          vertical-align: top;
          display: inline-block;
        }
      `

      printWin.document.open()
      printWin.document.write(
        '<!DOCTYPE html><html><head><meta charset="utf-8"><title>旅游合同</title>'
        + '<style>' + styles + '</style></head><body>'
        + paperEl.innerHTML
        + '</body></html>'
      )
      printWin.document.close()

      const doPrint = () => {
        printWin.focus()
        printWin.print()
        printWin.onafterprint = () => {
          printWin.close()
        }
        setTimeout(() => {
          if (!printWin.closed) {
            printWin.close()
          }
        }, 60000)
      }

      if (printWin.document.readyState === 'complete') {
        setTimeout(doPrint, 300)
      } else {
        printWin.onload = () => setTimeout(doPrint, 300)
      }
    },
    goBack() {
      window.close()
    },
  },
  mounted() {
    this.orderUuid = this.$route.query.orderUuid
    if (this.orderUuid) {
      this.loadContract()
    } else {
      this.$message.error('缺少订单号')
      setTimeout(() => {
        window.close()
      }, 1500)
    }
  },
}
</script>

<style scoped>
.contract-preview {
  padding: 20px;
  background: #f5f5f5;
  min-height: 100vh;
}

.header-actions {
  text-align: right;
  margin-bottom: 8px;
  padding: 15px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.capture-tip {
  margin: 0 0 12px;
  font-size: 12px;
  color: #909399;
  text-align: right;
}

.contract-content {
  max-width: 210mm;
  margin: 0 auto;
  background: white;
  padding: 12mm;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.contract-content.is-capturing {
  box-shadow: none;
  border-radius: 0;
}

.contract-paper {
  font-size: 12px;
  line-height: 1.6;
  color: #333;
}

.contract-paper :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
}

.contract-paper :deep(td),
.contract-paper :deep(th) {
  border: 1px solid #ddd;
  padding: 6px 10px;
  line-height: 1.5;
}

.contract-paper :deep(p) {
  margin: 0 0 6px 0;
}

.contract-paper :deep(.contract-seal-img) {
  max-width: 100px;
  height: auto;
  vertical-align: top;
  display: inline-block;
}

.no-content {
  padding: 60px 0;
  text-align: center;
}

@media print {
  .header-actions,
  .capture-tip {
    display: none;
  }

  .contract-preview {
    padding: 0;
    background: white;
  }

  .contract-content {
    padding: 12mm;
    box-shadow: none;
    border: none;
    max-width: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/contract/contract-preview.vue